import SubHeaders from '@/components/Headers/SubHeaders'
import { Button } from '@/components/ui/button'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getMyAcceptedRegulations } from '@/modules/Regulations/services'
import { MyAcceptedRegulation } from '@/modules/Regulations/types'
import { Attachment } from '@/modules/Upload/types'
import { formatDateWithFormatter } from '@/utils/function'
import { paths } from '@/utils/path'
import { Download, Eye, FileText, ScrollText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import NavigationCards from '../NavigationCards'

const getPdfAttachment = (regulation: MyAcceptedRegulation) =>
    regulation.attachments?.find(
        (attachment) => attachment.attachment_file_type === 'application/pdf'
    )

const formatDateTime = (value?: string | null) =>
    value
        ? formatDateWithFormatter(value, 'DD/MM/YYYY HH:mm', {
              fromUTC: true,
          })
        : '-'

const Regulations = () => {
    const { t } = useTranslation()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REGULATIONS, 'my-accepted'],
        queryFn: getMyAcceptedRegulations,
    })

    const acceptedRegulations = data?.results || []

    const handlePreview = (attachment: Attachment) => {
        openFilePreview(attachment)
    }

    const handleDownload = (attachment: Attachment) => {
        downloadFileWithPresigned(attachment)
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    { path: paths.account, title: t('account') },
                    {
                        path: '',
                        title: t(
                            'regulations:accepted-regulations',
                            'Accepted Policies'
                        ),
                    },
                ]}
            />

            <NavigationCards>
                {!acceptedRegulations.length ? (
                    <div className="py-8 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                            <ScrollText className="h-6 w-6 text-slate-500" />
                        </div>
                        <Typogaphy size="small" className="font-medium">
                            {t(
                                'regulations:no-accepted-regulation',
                                'No accepted policy found'
                            )}
                        </Typogaphy>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {acceptedRegulations.map((regulation) => {
                            const pdfAttachment = getPdfAttachment(regulation)
                            const statusLabel = regulation.is_active
                                ? t('active')
                                : t(
                                      'regulations:inactive-accepted-version',
                                      'No longer active'
                                  )

                            return (
                                <div
                                    key={regulation.acceptance_id}
                                    className="border-b border-slate-200 pb-4 last:border-b-0 last:pb-0"
                                >
                                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <Typogaphy
                                                size="small"
                                                className="font-medium"
                                            >
                                                {regulation.regulation_title}
                                            </Typogaphy>
                                            {regulation.regulation_summary && (
                                                <Typogaphy
                                                    size="small"
                                                    className="mt-1 text-gray-500"
                                                >
                                                    {
                                                        regulation.regulation_summary
                                                    }
                                                </Typogaphy>
                                            )}
                                        </div>
                                        <span
                                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                regulation.is_active
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : 'bg-slate-100 text-slate-600'
                                            }`}
                                        >
                                            {statusLabel}
                                        </span>
                                    </div>

                                    <div className="grid gap-3 md:grid-cols-4">
                                        <div>
                                            <Typogaphy
                                                size="small"
                                                className="text-gray-500"
                                            >
                                                {t(
                                                    'regulations:regulations-version'
                                                )}
                                            </Typogaphy>
                                            <Typogaphy
                                                size="small"
                                                className="font-medium"
                                            >
                                                v
                                                {regulation.regulation_version.replace(
                                                    /^v/i,
                                                    ''
                                                )}
                                            </Typogaphy>
                                        </div>
                                        <div>
                                            <Typogaphy
                                                size="small"
                                                className="text-gray-500"
                                            >
                                                {t(
                                                    'regulations:accepted-at',
                                                    'Accepted at'
                                                )}
                                            </Typogaphy>
                                            <Typogaphy
                                                size="small"
                                                className="font-medium"
                                            >
                                                {formatDateTime(
                                                    regulation.accepted_at
                                                )}
                                            </Typogaphy>
                                        </div>
                                        <div>
                                            <Typogaphy
                                                size="small"
                                                className="text-gray-500"
                                            >
                                                {t(
                                                    'regulations:last-updated-at',
                                                    'Last updated'
                                                )}
                                            </Typogaphy>
                                            <Typogaphy
                                                size="small"
                                                className="font-medium"
                                            >
                                                {formatDateTime(
                                                    regulation.updated_at
                                                )}
                                            </Typogaphy>
                                        </div>
                                        <div>
                                            <Typogaphy
                                                size="small"
                                                className="text-gray-500"
                                            >
                                                {t('status')}
                                            </Typogaphy>
                                            <Typogaphy
                                                size="small"
                                                className="font-medium"
                                            >
                                                {statusLabel}
                                            </Typogaphy>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        {pdfAttachment ? (
                                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2">
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                                                    <Typogaphy
                                                        size="small"
                                                        className="truncate"
                                                    >
                                                        {
                                                            pdfAttachment.attachment_file_name
                                                        }
                                                    </Typogaphy>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1"
                                                        onClick={() =>
                                                            handlePreview(
                                                                pdfAttachment
                                                            )
                                                        }
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                        {t('view')}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1"
                                                        onClick={() =>
                                                            handleDownload(
                                                                pdfAttachment
                                                            )
                                                        }
                                                    >
                                                        <Download className="h-4 w-4" />
                                                        {t('download')}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <Typogaphy
                                                size="small"
                                                className="text-gray-500"
                                            >
                                                {t(
                                                    'regulations:regulation-file-not-found'
                                                )}
                                            </Typogaphy>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </NavigationCards>
        </div>
    )
}

export default Regulations
